import CIcon from '@coreui/icons-react';
import {
  CButton, CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle,
} from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _uniq from 'lodash/uniq';

import { displayEventDate } from '../../core/helpers/date';
import { formatDate } from '../../core/helpers/default';
import useGlobalSelectionsContext from '../../core/hooks/useGlobalSelectionsContext';
import useQueryParams from '../../core/hooks/useQueryParams';
import { ServerSideTableSimple } from '../../Shared/ServerSideTable';
import { fetchProspects, postGenerateRoute } from './actions';
import { attachPetitionToProspect, detachProspectFromPettion } from './api.service';
import { linkedTableFields, tableFields } from './constants';
import ProspectsFilter from './Filter';
import PetitionsModal from './SelectPetition';
import ProspectLinesModal from './SelectProspectLines';
import useNotifications from '../Notifications/useNotifications';
import StartLocationModal from './StartLocationModal';
import ChatDialog from '../Recruits/RecruitsPage/ChatDialog';

import '../Recruits/RecruitsPage/styles.scss';

const ManageProspects = () => {
  const {
    data,
    page,
    lastPage,
    perPage,
    loading,
    search,
    sort,
    order,
  } = useSelector((state) => state.manageProspects.prospects);
  const { user } = useSelector((state) => state.auth.auth);
  const { showError } = useNotifications();
  const {
    initiativeId, organizationId, refetchCurrentPageData, initiativeSettings,
  } = useGlobalSelectionsContext();
  const { getParam } = useQueryParams();
  const initialSelectedProspect = getParam('prospect');
  // eslint-disable-next-line max-len
  const makeInitialSelectedProspects = () => (initialSelectedProspect ? [Number(initialSelectedProspect)] : []);
  const [onlyUnlinkedProspects, changeOnlyUnlinkedProspects] = useState(true);
  const [filters, changeFilters] = useState({});
  const [showPetitions, changeShowPetitions] = useState(window.location.hash === '#modal-petitions');
  const [selectedProspects, changeSelectedProspects] = useState(makeInitialSelectedProspects);
  const [selectedPetition, changeSelectedPetition] = useState();
  const [isStartLocationModalOpen, setStartLocationModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [previousChecked, changePreviousChecked] = React.useState(null);

  const normalizedData = data.map((item) => ({
    ...item,
    date_created: formatDate(item.date_created),
    _selected: selectedProspects.includes(item.id),
    event: item.event ? displayEventDate(item.event) : '',
    petition: item.petition?.local_id,
  }));

  const dispatch = useDispatch();

  const query = {
    ...filters,
    search,
    sort,
    order,
    page,
    lastPage,
    perPage,
    has_method: 1,
    [onlyUnlinkedProspects ? 'no_petitions' : 'has_petitions']: 1,
    initiative_id: initiativeId,
    organization_id: organizationId,
  };

  const onTableFetch = (queryVal) => {
    dispatch(fetchProspects(queryVal));
  };

  const onTableRowCheck = (e, prospectId) => {
    const i = selectedProspects.indexOf(prospectId);
    if (e.target.checked && i === -1) {
      changeSelectedProspects([...selectedProspects, prospectId]);
    } else if (!e.target.checked && i >= 0) {
      const newVal = [...selectedProspects];
      newVal.splice(i, 1);
      changeSelectedProspects(newVal);
    }
  };

  const onSelectAll = (selectAll) => {
    changeSelectedProspects(selectAll ? data.map(({ id }) => id) : makeInitialSelectedProspects());
  };

  const handleSelectPetition = (petition) => {
    changeSelectedPetition(petition);
    changeShowPetitions(false);
  };

  const resetPreviousChecked = () => changePreviousChecked(!previousChecked);

  const onSelectClick = (e, id) => {
    e.stopPropagation();
    if (e.shiftKey) {
      const prospectsIdsToCheck = [];
      const prevIndex = data.findIndex((el) => el.id === previousChecked);
      const nextIndex = data.findIndex((el) => el.id === id);
      data.forEach((el, key) => {
        if (key >= prevIndex && key <= nextIndex) {
          prospectsIdsToCheck.push(el.id);
        }
      });
      changeSelectedProspects(_uniq([...selectedProspects, ...prospectsIdsToCheck]));
      resetPreviousChecked();
    } else {
      changePreviousChecked(id);
    }
  };

  const handleSubmit = (prospectsLines) => {
    Promise.all(selectedProspects.map((prospectId) => (
      attachPetitionToProspect(prospectId, selectedPetition.id, prospectsLines[prospectId])
    ))).then(() => {
      onTableFetch(query);
      changeSelectedPetition(undefined);
      changeSelectedProspects([]);
    });
  };

  const handleUnlinkProspects = () => {
    Promise.all(selectedProspects.map((prospectId) => (
      detachProspectFromPettion(prospectId)
    ))).then(() => {
      onTableFetch(query);
      changeSelectedPetition(undefined);
      changeSelectedProspects([]);
    });
  };

  const getPreselectedPetitionId = () => {
    if (selectedProspects.length === 1) {
      const prospect = data.find(({ id }) => id === selectedProspects[0]);
      return prospect?.petition?.id;
    }
    return undefined;
  };

  useEffect(() => {
    onTableFetch(query);
    changeSelectedProspects([]);
    // eslint-disable-next-line
  }, [filters, onlyUnlinkedProspects, refetchCurrentPageData]);

  const onGenerateRouteClick = () => {
    if (data.every((el) => {
      if (selectedProspects.some((id) => +id === +el.id)) {
        return !!el.lat && !!el.lng;
      }
      return true;
    })) {
      setStartLocationModalOpen(true);
    } else {
      showError('Some of the prospects you selected do not have lat/long coordinates');
    }
  };

  const onSubmitWalkRoute = async (address) => {
    await dispatch(postGenerateRoute({
      prospects: selectedProspects,
      initiative_id: initiativeId,
      organization_id: organizationId,
      user_id: user.id,
      start_lng: address.lng,
      start_lat: address.lat,
    }));
    setStartLocationModalOpen(false);
    onTableFetch(query);
    changeSelectedPetition(undefined);
    changeSelectedProspects([]);
  };

  const handleChatModal = (prospect = null) => setSelectedChat(prospect);

  return (
    <>
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <h3 className="mb-0">
            {onlyUnlinkedProspects ? 'Unlinked' : 'Linked'}
            {' '}
            Prospects:
          </h3>
          <CDropdown className="MethodStatusFilter">
            <CDropdownToggle color="transparent" className="text-body" caret={false}>
              <CIcon name="cil-list" />
              <CIcon name="cil-swap-vertical" />
            </CDropdownToggle>
            <CDropdownMenu>
              <CDropdownItem onClick={() => changeOnlyUnlinkedProspects((val) => !val)}>
                {onlyUnlinkedProspects ? 'Display Linked Prospects' : 'Display Unlinked Prospects'}
              </CDropdownItem>
            </CDropdownMenu>
          </CDropdown>
        </div>
        <ProspectsFilter filters={filters} onChangeFilters={changeFilters} />
      </div>
      <ServerSideTableSimple
        query={query}
        onFetch={onTableFetch}
        loading={loading}
        fields={onlyUnlinkedProspects ? tableFields : linkedTableFields}
        data={normalizedData}
        tableFilter
        onRowCheck={onTableRowCheck}
        onSelectAll={onSelectAll}
        selectType="checkbox"
        itemsPerPageSelect={{ label: 'Items per page:', values: [50, 10, 20, 100] }}
        onCustomSelectClick={onSelectClick}
        scopedSlots={{
          email: (item) => <td className="user-select-none">{item.email || '—'}</td>,
          first_name: (item) => <td className="user-select-none">{item.first_name}</td>,
          last_name: (item) => <td className="user-select-none">{item.last_name}</td>,
          method: (item) => <td className="user-select-none">{item.method}</td>,
          event: (item) => <td className="user-select-none">{item.event}</td>,
          location: (item) => <td className="user-select-none">{item.location}</td>,
          assigned_to: (item) => <td className="user-select-none">{item.assigned_to}</td>,
          date_created: (item) => <td className="user-select-none">{item.date_created}</td>,
          actions: (item) => (
            <td className="user-select-none">
              {!!item.phone_number && !!initiativeSettings.recruits_enabled && (
                <button
                  type="button"
                  onClick={() => handleChatModal(item)}
                  className="chat-icon"
                  data-test-id="chat-btn"
                >
                  <CIcon name="cil-chat-bubble" alt="chat" />
                  <div className="chat-icon__unread-count">{item.count_unread_messages || 0}</div>
                </button>
              )}
            </td>
          ),
        }}
      />
      {showPetitions && (
        <PetitionsModal
          preselectedPetition={getPreselectedPetitionId()}
          show={showPetitions}
          onClose={() => changeShowPetitions(false)}
          onSubmit={handleSelectPetition}
        />
      )}
      {selectedPetition && (
        <ProspectLinesModal
          prospectIds={selectedProspects}
          petition={selectedPetition}
          show={!!selectedPetition}
          onSubmit={handleSubmit}
          onClose={() => changeSelectedPetition(undefined)}
        />
      )}
      {selectedProspects.length > 0 && onlyUnlinkedProspects && (
        <CButton
          color="primary"
          className="m-2"
          onClick={() => changeShowPetitions(true)}
        >
          Link to Petition
        </CButton>
      )}
      {selectedProspects.length > 0 && !onlyUnlinkedProspects && (
        <div>
          <CButton
            color="primary"
            className="m-2"
            onClick={() => changeShowPetitions(true)}
          >
            Link to Different Petition
          </CButton>
          <CButton
            color="primary"
            className="m-2"
            onClick={() => handleUnlinkProspects(selectedProspects)}
          >
            Unlink Petition
          </CButton>
        </div>
      )}
      {selectedProspects.length > 0 && onlyUnlinkedProspects && filters.prospect_location && (
      <CButton
        color="primary"
        className="m-2"
        onClick={onGenerateRouteClick}
      >
        Generate Route
      </CButton>
      )}
      <StartLocationModal
        show={isStartLocationModalOpen}
        onConfirm={onSubmitWalkRoute}
        onClose={() => setStartLocationModalOpen(false)}
      />
      {!!selectedChat && (
        <ChatDialog
          chatID={selectedChat.id}
          recruitName={selectedChat.first_name}
          onClose={handleChatModal}
        />
      )}
    </>
  );
};

export default ManageProspects;
